import { Howl } from 'howler';

/** Stable keys for the six Tier-2 sound effects. */
export type AudioKey = 'shot' | 'save' | 'goal' | 'off' | 'tap' | 'reveal';

const ALL_KEYS: readonly AudioKey[] = ['shot', 'save', 'goal', 'off', 'tap', 'reveal'];

export interface AudioModule {
  init(): void;
  play(key: AudioKey): void;
  setEnabled(v: boolean): void;
  isEnabled(): boolean;
}

interface MinimalPrefs {
  getSoundEnabled(): boolean;
  setSoundEnabled(v: boolean): void;
}

/**
 * Create the audio module bound to a prefs store. Single instance per app —
 * call createAudio(prefs) once in main.tsx, share via context or import.
 */
export function createAudio(prefs: MinimalPrefs): AudioModule {
  let howls: Map<AudioKey, Howl> | null = null;

  function init(): void {
    if (howls) return; // idempotent
    howls = new Map();
    for (const key of ALL_KEYS) {
      howls.set(key, new Howl({ src: [`/audio/${key}.ogg`], preload: true, volume: 1 }));
    }
  }

  function play(key: AudioKey): void {
    if (!howls) return;            // pre-init no-op
    if (!prefs.getSoundEnabled()) return; // disabled no-op
    const h = howls.get(key);
    if (!h) return;                // unknown key no-op
    try { h.play(); } catch { /* swallow — never let audio kill UI */ }
  }

  function setEnabled(v: boolean): void { prefs.setSoundEnabled(v); }
  function isEnabled(): boolean { return prefs.getSoundEnabled(); }

  return { init, play, setEnabled, isEnabled };
}
